/**
 * Vista de aceptación (estado cotizado): documento digital con header, datos, bloques, condiciones y firma.
 * El cliente ve Aceptar / Pedir cambio (modal) / Descargar PDF.
 */

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { QuotationBlocksView } from '@/components/store/QuotationBlocksView';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import type { QuotationCartLine } from '@/hooks/use-quotation-cart';
import type { QuotationDocBlocks, QuotationRequestStatus, SourcingItem } from '@/lib/store-types';
import type { Lead } from '@/lib/store-types';
import { computeGlobalTotalPrice, computeBlockPriceSummary, isFullContainerBlock } from '@/pages/store/store-cart-sections';
import { FileDown, CheckCircle, XCircle, ShieldCheck, Edit3, Loader2, PartyPopper, Copy } from 'lucide-react';

const COMPANY_NAME = 'Padmora';

/** Formatea el precio como moneda (USD) si es un número; si no, devuelve el texto tal cual (ej. "a consultar"). */
function formatPriceText(value: string): string {
  const n = Number(String(value).replace(/\s/g, '').replace(/,/g, '.'));
  if (Number.isFinite(n)) {
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return value;
}

export interface QuotationAcceptanceViewProps {
  /** Request id for respond API and optional PDF link */
  requestId: string;
  /** Request blocks (for conditions text and QuotationBlocksView) */
  blocks?: QuotationDocBlocks | null;
  /** Cart lines for blocks view */
  items: QuotationCartLine[];
  /** Status of the quotation request (used to decide when to show final line prices). */
  status?: QuotationRequestStatus;
  /** Lead/destinatary (importer data) */
  lead?: Lead | null;
  /** Incoterm and destination from request */
  incoterm?: string | null;
  destinationPortOrAddress?: string | null;
  /** Document-level fields from request (datos de cotización) */
  addressee_company?: string | null;
  addressee_contact?: string | null;
  final_price_text?: string | null;
  payment_type?: string | null;
  payment_method?: string | null;
  observaciones?: string | null;
  /** Document display fields for digital offer (muestra de oferta digital). */
  documentTitle?: string | null;
  headerImageUrl?: string | null;
  sealImageUrl?: string | null;
  finalMessage?: string | null;
  showFinalMessage?: boolean | null;
  /** Unique quotation reference/code shown to client and admin. */
  quotationCode?: string | null;
  /** Whether to show Accept/Reject buttons (client view); hide in admin view */
  showActions?: boolean;
  /** Already responded (finished) */
  responded?: boolean;
  outcome?: 'accepted' | 'rejected' | null;
  /** Call when client accepts */
  onAccept?: () => void;
  /** Call when client requests changes (comment sent to backend; quotation goes back to en_revision) */
  onRequestChange?: (comment: string) => void | Promise<void>;
  /** Loading state for respond mutation */
  respondPending?: boolean;
  /** Optional: sourcing items and loading methods for mix block fill bars */
  allSourcingItems?: { id: string }[];
  loadingMethods?: { id: string; name: string }[];
  /** Generar y descargar PDF de la cotización (mismo formato que el preview). Retorna URL del PDF o null. */
  onDownloadQuotationPdf?: () => Promise<string | null>;
  /** Mientras se genera el PDF (llamada en curso). */
  pdfDownloadPending?: boolean;
  /** Sección de documentación (debajo de datos, encima del preview de bloques). */
  documentationSection?: React.ReactNode;
  /** Acción principal cuando la pelota está en el tejado del cliente (ej. "Subir comprobante de depósito"). Se muestra en el header donde estaba Aceptar cotización. */
  primaryAction?: React.ReactNode;
  /** Línea full-width de datos bancarios (debajo de cabecera y botones, dentro del preview). */
  bankDetailsSection?: React.ReactNode;
  /** Duplicar solicitud (copiar a nueva en draft). Se muestra botón "Copiar a nueva solicitud" cuando hay post-aceptación. */
  onDuplicate?: () => void | Promise<void>;
  duplicatePending?: boolean;
  /** Vista admin: en Destinatario se muestra un enlace al lead (mailto). */
  isAdminView?: boolean;
}

export function QuotationAcceptanceView({
  requestId,
  blocks,
  items,
  status,
  lead,
  incoterm,
  destinationPortOrAddress,
  addressee_company,
  addressee_contact,
  final_price_text,
  payment_type,
  payment_method,
  observaciones,
  documentTitle,
  headerImageUrl,
  sealImageUrl,
  finalMessage,
  showFinalMessage,
  quotationCode,
  showActions = true,
  responded = false,
  outcome,
  onAccept,
  onRequestChange,
  respondPending = false,
  allSourcingItems = [],
  loadingMethods = [],
  onDownloadQuotationPdf,
  pdfDownloadPending = false,
  documentationSection,
  primaryAction,
  bankDetailsSection,
  onDuplicate,
  duplicatePending = false,
  isAdminView = false,
}: QuotationAcceptanceViewProps) {
  const [requestChangeOpen, setRequestChangeOpen] = useState(false);
  const [requestChangeComment, setRequestChangeComment] = useState('');
  const conditionsText = blocks?.general?.specification ?? '';
  const title = documentTitle || 'Cotización';
  const showFinalLinePrice = status === 'cotizado' || responded;


  // Regla de negocio: el precio final se calcula como suma de precios parciales de todos los contenedores.
  // Usa la misma lógica que el PDF (computeGlobalTotalPrice).
  const computedTotalPrice = useMemo(
    () => computeGlobalTotalPrice(blocks ?? undefined, allSourcingItems as SourcingItem[], loadingMethods),
    [blocks, allSourcingItems, loadingMethods]
  );
  // Siempre mostrar un precio: calculado, fallback a final_price_text, o "a consultar".
  const mixBlocks = blocks?.mix ?? [];
  const hasMixBlocks = mixBlocks.some((b) => (b.rows ?? []).length > 0);
  const effectivePriceText = computedTotalPrice != null && computedTotalPrice > 0
    ? computedTotalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : final_price_text
      ? formatPriceText(final_price_text)
      : hasMixBlocks ? 'a consultar' : null;
  const effectivePriceIsNumeric = effectivePriceText != null && effectivePriceText !== 'a consultar';

  // Precio parcial por cada bloque mix (para mostrar desglose en Resumen económico).
  const blockPriceSummaries = useMemo(() => {
    if (!hasMixBlocks) return [];
    return mixBlocks
      .filter((b) => (b.rows ?? []).length > 0)
      .map((b, i) => ({
        label: b.name ?? (isFullContainerBlock(b) ? 'Bloque completo' : `Bloque mixto ${i + 1}`),
        numContainers: Math.max(1, b.num_containers ?? 1),
        summary: computeBlockPriceSummary(b, allSourcingItems as SourcingItem[], loadingMethods),
      }));
  }, [hasMixBlocks, mixBlocks, allSourcingItems, loadingMethods]);

  const hasDocumentFields = addressee_company || addressee_contact || effectivePriceText || payment_type || payment_method || observaciones;

  return (
    <div className="w-full min-w-0 flex-1 space-y-6 rounded-2xl border-2 border-zinc-200 bg-white shadow-lg overflow-hidden">
      {/* Cabecera: título + referencia a la izquierda; a la derecha: Aceptar/Rechazar y Descargar PDF. */}
      <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-zinc-900">{COMPANY_NAME}</h1>
          <p className="text-sm text-zinc-600 mt-0.5">{title}</p>
          {quotationCode && (
            <p className="text-xs text-zinc-500 mt-0.5">Referencia: {quotationCode}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          {primaryAction ? (
            primaryAction
          ) : responded ? (
            <div className="flex items-center gap-2 text-sm font-medium">
              {outcome === 'accepted' && status === 'in_process' ? (
                <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-3">
                  <div
                    className="h-10 w-10 shrink-0 rounded-full border-2 border-emerald-200 border-t-emerald-600 animate-spin"
                    aria-hidden
                  />
                  <span className="text-emerald-800 font-medium text-center sm:text-left">
                    Estamos procesando su solicitud en estos momentos
                  </span>
                </div>
              ) : outcome === 'accepted' && status === 'arriving_soon' ? (
                <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-3">
                  <div
                    className="h-10 w-10 shrink-0 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin"
                    aria-hidden
                  />
                  <span className="text-blue-800 font-medium text-center sm:text-left">
                    En tránsito
                  </span>
                </div>
              ) : outcome === 'accepted' && (status === 'delivered' || status === 'finished') ? (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex items-center gap-2">
                    <PartyPopper className="h-8 w-8 text-amber-500" aria-hidden />
                    <span className="text-zinc-800 font-medium">
                      ¡Muchas gracias por confiar en nosotros!
                    </span>
                  </div>
                  {onDuplicate && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5 shrink-0"
                      disabled={duplicatePending}
                      onClick={() => onDuplicate()}
                    >
                      {duplicatePending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                      {duplicatePending ? 'Creando…' : 'Copiar a nueva solicitud'}
                    </Button>
                  )}
                </div>
              ) : outcome === 'accepted' ? (
                <>
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <span className="text-emerald-800">Cotización aceptada</span>
                  {onDuplicate && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5 shrink-0"
                      disabled={duplicatePending}
                      onClick={() => onDuplicate()}
                    >
                      {duplicatePending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                      {duplicatePending ? 'Creando…' : 'Copiar a nueva solicitud'}
                    </Button>
                  )}
                </>
              ) : null}
              {outcome === 'rejected' && (
                <>
                  <XCircle className="h-5 w-5 text-amber-600" />
                  <span className="text-amber-800">Cotización rechazada</span>
                </>
              )}
            </div>
          ) : showActions ? (
            <>
              <Button
                type="button"
                onClick={onAccept}
                disabled={respondPending}
                className="gap-2 btn-accept-highlight"
              >
                <CheckCircle className="h-4 w-4" />
                Aceptar cotización
              </Button>
              {onDownloadQuotationPdf && (
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2 border-primary text-primary hover:bg-primary/10 hover:text-primary"
                  disabled={pdfDownloadPending}
                  onClick={async () => {
                    const url = await onDownloadQuotationPdf();
                    if (url) window.open(url, '_blank');
                  }}
                >
                  {pdfDownloadPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                  {pdfDownloadPending ? 'Generando PDF…' : 'Descargar PDF cotización'}
                </Button>
              )}
              {onRequestChange && (
                <Dialog open={requestChangeOpen} onOpenChange={setRequestChangeOpen}>
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={respondPending}
                      className="gap-2 border-amber-300 text-amber-800 hover:bg-amber-50 hover:text-amber-900"
                    >
                      <Edit3 className="h-4 w-4" />
                      Pedir cambio
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Solicitar cambios a la cotización</DialogTitle>
                    </DialogHeader>
                    <DialogDescription className="text-sm text-zinc-600">
                      Indica qué cambios necesitas. La cotización volverá a revisión y el equipo te contactará.
                    </DialogDescription>
                    <Textarea
                      placeholder="Ej.: Ajustar cantidad del producto X, cambiar incoterm a DDP, añadir partida Y..."
                      value={requestChangeComment}
                      onChange={(e) => setRequestChangeComment(e.target.value)}
                      className="min-h-[120px] resize-y"
                      maxLength={2000}
                    />
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setRequestChangeOpen(false)}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        disabled={!requestChangeComment.trim() || respondPending}
                        onClick={async () => {
                          const comment = requestChangeComment.trim();
                          if (!comment) return;
                          await onRequestChange(comment);
                          setRequestChangeComment('');
                          setRequestChangeOpen(false);
                        }}
                      >
                        Solicitar cambios
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </>
          ) : onDownloadQuotationPdf ? (
            <Button
              type="button"
              variant="outline"
              className="gap-2 border-primary text-primary hover:bg-primary/10 hover:text-primary"
              disabled={pdfDownloadPending}
              onClick={async () => {
                const url = await onDownloadQuotationPdf();
                if (url) window.open(url, '_blank');
              }}
            >
              {pdfDownloadPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              {pdfDownloadPending ? 'Generando PDF…' : 'Descargar PDF cotización'}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Datos bancarios: línea full-width justo debajo de cabecera y botones (Subir comprobante / Descargar factura). */}
      {bankDetailsSection && (
        <div className="w-full border-b border-zinc-200 bg-white px-6 py-4">
          {bankDetailsSection}
        </div>
      )}

      {/* Bloque de datos: destinatario, condiciones de pago y resumen económico. Diseño legible (jerarquía clara, buen espaciado). */}
      <div className="px-6 py-6 border-b border-zinc-100 bg-zinc-50/50">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Tarjeta 1: Destinatario */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-bold text-zinc-700 mb-4">
              Destinatario
            </h2>
            <div className="space-y-4">
              {(addressee_company ?? lead?.company) && (
                <div>
                  <p className="text-xs font-medium text-zinc-500 mb-0.5">Empresa</p>
                  <p className="text-base text-zinc-900 font-medium leading-snug">
                    {addressee_company ?? lead?.company}
                  </p>
                </div>
              )}
              {(addressee_contact ?? lead?.name) && (
                <div>
                  <p className="text-xs font-medium text-zinc-500 mb-0.5">Contacto</p>
                  <p className="text-base text-zinc-900 leading-snug">
                    {addressee_contact ?? lead?.name}
                  </p>
                </div>
              )}
              {(lead?.phone ?? lead?.whatsapp) && (
                <div>
                  <p className="text-xs font-medium text-zinc-500 mb-0.5">Teléfono</p>
                  <p className="text-base text-zinc-900 leading-snug">
                    {lead.phone ?? lead.whatsapp}
                  </p>
                </div>
              )}
              {lead?.email && (
                <div>
                  <p className="text-xs font-medium text-zinc-500 mb-0.5">Email</p>
                  <p className="text-base text-zinc-900 break-all leading-snug">
                    {lead.email}
                  </p>
                </div>
              )}
              {(lead?.city ?? lead?.country) && (
                <div>
                  <p className="text-xs font-medium text-zinc-500 mb-0.5">Dirección</p>
                  <p className="text-base text-zinc-900 leading-snug">
                    {[lead.city, lead.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              )}
              {isAdminView && lead && (
                <div className="pt-2 border-t border-zinc-100">
                  <a
                    href={`mailto:${lead.email ?? ''}`}
                    className="text-xs text-primary hover:underline"
                  >
                    Ver lead: {lead.name ?? lead.email} ({lead.email})
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Tarjeta 2: Condiciones de pago + concepto de transferencia */}
          {hasDocumentFields && (
            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-bold text-zinc-700 mb-4">
                Condiciones de pago
              </h2>
              <div className="space-y-4">
                {payment_type && (
                  <div>
                    <p className="text-xs font-medium text-zinc-500 mb-0.5">Tipo de pago</p>
                    <p className="text-base text-zinc-900 leading-snug">{payment_type}</p>
                  </div>
                )}
                {payment_method && (
                  <div>
                    <p className="text-xs font-medium text-zinc-500 mb-0.5">Método de pago</p>
                    <p className="text-base text-zinc-900 leading-snug">{payment_method}</p>
                  </div>
                )}
                {observaciones && (
                  <div>
                    <p className="text-xs font-medium text-zinc-500 mb-0.5">Observaciones</p>
                    <p className="text-base text-zinc-800 whitespace-pre-wrap leading-snug">
                      {observaciones}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tarjeta 3: Resumen económico — precio total + desglose parcial por contenedor */}
          {(effectivePriceText || destinationPortOrAddress || incoterm || blockPriceSummaries.length > 0) && (
            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <h2 className="text-base font-bold text-zinc-700 mb-4">
                Resumen económico
              </h2>

              {/* Desglose parcial por bloque/contenedor */}
              {blockPriceSummaries.length > 0 && (
                <div className="space-y-2 mb-4">
                  {blockPriceSummaries.map((bs, i) => (
                    <div key={i} className="flex items-baseline justify-between gap-2 text-sm">
                      <span className="text-zinc-600 truncate">
                        {bs.label}
                        {bs.numContainers > 1 && (
                          <span className="text-zinc-400 ml-1">×{bs.numContainers}</span>
                        )}
                      </span>
                      <span className="font-medium tabular-nums text-zinc-800 whitespace-nowrap">
                        {bs.summary.partialPriceUsd != null
                          ? `USD ${bs.summary.partialPriceUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : 'a consultar'}
                      </span>
                    </div>
                  ))}
                  {blockPriceSummaries.length > 1 && <div className="border-t border-zinc-200 pt-1" />}
                </div>
              )}

              {/* Precio total */}
              {effectivePriceText && (
                effectivePriceIsNumeric ? (
                  <p className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-4">
                    <span className="text-lg font-medium text-zinc-600 mr-1">USD</span>
                    {effectivePriceText}
                  </p>
                ) : (
                  <p className="text-xl font-semibold text-amber-700 mb-4">
                    Precio total: {effectivePriceText}
                  </p>
                )
              )}

              <div className="space-y-4 pt-4 border-t border-zinc-200">
                {incoterm && (
                  <div>
                    <p className="text-sm font-medium text-zinc-500 mb-1">Incoterm</p>
                    <p className="text-lg font-semibold text-zinc-800">
                      {incoterm}
                    </p>
                  </div>
                )}
                {destinationPortOrAddress && (
                  <div>
                    <p className="text-sm font-medium text-zinc-500 mb-1">Destino</p>
                    <p className="text-lg font-medium text-zinc-800 leading-snug">
                      {destinationPortOrAddress}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {documentationSection && (
        <div className="px-6 py-3 border-b border-zinc-100">
          {documentationSection}
        </div>
      )}

      {/* Bloques y precios (contenedores): siempre visible. */}
      <div className="px-6 py-3">
        <QuotationBlocksView
          request={{ blocks: blocks ?? undefined, status }}
          items={items}
          mode="admin-view"
          readOnly
          adminProps={{
            showFinalLinePrice,
            allSourcingItems: allSourcingItems as { id: string; [k: string]: unknown }[],
            loadingMethods,
          }}
        />
      </div>

      {/* Condiciones */}
      {conditionsText && (
        <div className="px-6 py-3 border-t border-zinc-100">
          <h2 className="text-sm font-semibold text-zinc-700 mb-2">Condiciones</h2>
          <p className="text-sm text-zinc-800 whitespace-pre-wrap">{conditionsText}</p>
        </div>
      )}

      {/* Mensaje final y firma/sello. Orden (izq → der): sello, firma digital, cabecera al extremo derecho. */}
      <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50">
        {showFinalMessage && finalMessage && (
          <p className="text-xs text-zinc-600 mb-2 whitespace-pre-wrap">
            {finalMessage}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex shrink-0 items-center gap-4">
            {sealImageUrl && (
              <img
                src={sealImageUrl}
                alt="Sello digital Padmora"
                className="h-52 w-52 shrink-0 rounded-xl border-2 border-blue-500 object-cover"
              />
            )}
            <div className="min-w-0 flex items-start gap-3">
              <ShieldCheck className="h-8 w-8 shrink-0 text-blue-600 mt-0.5" aria-hidden />
              <div>
                <p className="text-base font-semibold text-zinc-800">Firma digital — Equipo {COMPANY_NAME}</p>
                <p className="text-sm text-zinc-500 mt-1">Firmado digitalmente y legalmente vinculante.</p>
              </div>
            </div>
          </div>
          {headerImageUrl && (
            <img
              src={headerImageUrl}
              alt="Cabecera"
              className="max-h-64 max-w-[720px] w-auto h-auto shrink-0 object-contain block"
            />
          )}
        </div>
      </div>

    </div>
  );
}
